import { environment } from '../../../../environments/environment';
import { effect, Injector, signal } from '@angular/core';
import * as amplitude from '@amplitude/analytics-browser';
import { AnalyticsTrackerProvider } from '../analytics-tracker-provider';
import { AnalyticsUser } from '../analytics-user';
import { AnalyticsEvent } from '../analytics-event';

export class AmplitudeProvider implements AnalyticsTrackerProvider {
  private static readonly API_KEY = environment.amplitudeApiKey;

  private _active = signal<boolean>(false);
  private _initialized = signal<boolean>(false);

  readonly user = signal<AnalyticsUser | undefined>(undefined);

  get active() {
    return this._active.asReadonly();
  }

  get initialized() {
    return this._initialized.asReadonly();
  }

  get id() {
    return 'amplitude';
  }

  constructor(injector: Injector) {
    effect(
      () => {
        if (this._initialized() && this._active() && this.user() !== undefined) {
          this.identify();
        }
      },
      { injector }
    );
  }

  activate(): void {
    if (!this._initialized()) {
      return;
    }

    amplitude.setOptOut(false);

    this._active.set(true);
  }

  deactivate(): void {
    if (!this._initialized()) {
      return;
    }

    amplitude.setOptOut(true);

    // Clear Identity: Ensures local user and device identifiers (Device ID/User ID)
    // are erased from the browser, effectively anonymizing the device for future compliance.
    amplitude.reset();

    this._active.set(false);
  }

  initialize(): void {
    if (!AmplitudeProvider.API_KEY) {
      return;
    }

    if (this._initialized()) {
      return;
    }

    /** @see https://amplitude.com/docs/sdks/analytics/browser/browser-sdk-2#configure-the-sdk */
    amplitude.init(AmplitudeProvider.API_KEY, {
      serverZone: 'EU',
      autocapture: {
        pageViews: false,
        elementInteractions: true,
      },
    });

    this._initialized.set(true);

    this.deactivate();
  }

  sendEvent<T extends Record<string, unknown>>(event: AnalyticsEvent<T>): void {
    if (!this._initialized() || !this._active()) {
      return;
    }

    amplitude.track(event.name, event.props);
  }

  private identify(): void {
    const identifyEvent = new amplitude.Identify();

    for (const [key, value] of Object.entries(this.user())) {
      if (key !== 'id') {
        identifyEvent.set(key, value);
      }
    }

    amplitude.setUserId(this.user().id);
    amplitude.identify(identifyEvent);
  }
}
